import { useEffect, useState } from "react";
import { Inbox, Download, Mail, Building2, Clock } from "lucide-react";
import { api } from "@/lib/api";

const STATUSES = [
  { id: "nuevo", label: "Nuevo", color: "#00f0ff" },
  { id: "contactado", label: "Contactado", color: "#ffd166" },
  { id: "cerrado", label: "Cerrado", color: "#22e07a" },
];

export const Leads = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/leads");
      setLeads(data);
    } catch (e) {
      console.error("No se pudieron cargar los leads:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const setStatus = async (id, status) => {
    setLeads((prev) => prev.map((l) => (l.id === id ? { ...l, status } : l)));
    try {
      await api.patch(`/admin/leads/${id}`, { status });
    } catch (e) {
      console.error("No se pudo actualizar el estado:", e);
    }
  };

  const exportCSV = () => {
    const headers = ["Fecha", "Nombre", "Email", "Empresa", "Servicio", "Estado", "Mensaje"];
    const rows = leads.map((l) => [
      l.created_at || "",
      l.name || "",
      l.email || "",
      l.company || "",
      l.scope || "",
      l.status || "nuevo",
      (l.message || "").replace(/\n/g, " "),
    ]);
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const csv = [headers, ...rows].map((r) => r.map(esc).join(",")).join("\n");
    const blob = new Blob(["\ufeff" + csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `cyvr-leads-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-8" data-testid="leads-panel">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h3 className="flex items-center gap-2 font-head text-lg font-bold text-white">
          <Inbox className="h-5 w-5 text-cyber-cyan" /> Leads recibidos ({leads.length})
        </h3>
        <button
          onClick={exportCSV}
          data-testid="leads-export-csv"
          disabled={!leads.length}
          className="inline-flex items-center gap-2 rounded-full border border-white/15 px-4 py-2 font-mono-tech text-[11px] font-bold uppercase tracking-widest text-white/70 transition-colors hover:border-cyber-cyan hover:text-cyber-cyan disabled:opacity-40"
        >
          <Download className="h-3.5 w-3.5" /> Exportar CSV
        </button>
      </div>

      {loading ? (
        <p className="mt-6 font-sans text-sm text-white/40">Cargando leads…</p>
      ) : leads.length === 0 ? (
        <p className="mt-6 font-sans text-sm text-white/40" data-testid="leads-empty">Aún no hay leads. Cuando alguien rellene el formulario de contacto aparecerá aquí.</p>
      ) : (
        <div className="mt-6 space-y-4">
          {leads.map((l) => (
            <div
              key={l.id}
              data-testid="lead-item"
              className="rounded-2xl border border-white/10 bg-white/[0.03] p-5"
            >
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-head text-base font-bold text-white">{l.name}</p>
                  <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 font-mono-tech text-[11px] text-white/50">
                    <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 hover:text-cyber-cyan"><Mail className="h-3 w-3" /> {l.email}</a>
                    {l.company && <span className="inline-flex items-center gap-1"><Building2 className="h-3 w-3" /> {l.company}</span>}
                    {l.created_at && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3" /> {new Date(l.created_at).toLocaleString("es-ES")}</span>}
                  </div>
                </div>
                <div className="flex gap-1 rounded-full border border-white/10 bg-white/[0.03] p-1">
                  {STATUSES.map((s) => (
                    <button
                      key={s.id}
                      onClick={() => setStatus(l.id, s.id)}
                      data-testid={`lead-status-${s.id}`}
                      className="rounded-full px-3 py-1 font-mono-tech text-[10px] font-bold uppercase tracking-widest transition-colors"
                      style={
                        (l.status || "nuevo") === s.id
                          ? { background: s.color, color: "#04060a" }
                          : { color: "rgba(255,255,255,0.5)" }
                      }
                    >
                      {s.label}
                    </button>
                  ))}
                </div>
              </div>
              {l.scope && <p className="mt-3 font-mono-tech text-[10px] uppercase tracking-widest text-cyber-cyan">{l.scope}</p>}
              <p className="mt-2 whitespace-pre-wrap font-sans text-sm leading-relaxed text-white/70">{l.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
